import type { HTMLAttributes, ThHTMLAttributes } from 'react';
import clsx from 'clsx';

import type { TableBodyType, TableCellType, TableColumnType, TableFooterType, TableHeadType } from '../Table';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableFooter,
  TableHead,
  TableRow,
  TableScrollContainer,
} from '../Table';

import styles from './styles/StatsTable.module.scss';

const StatsTable = ({
  caption,
  cellWidth = 60,
  children,
  className,
  maxRows,
  ...props
}: {
  caption: string;
  cellWidth?: number;
  maxRows?: number;
} & HTMLAttributes<HTMLDivElement>) => {
  return (
    <TableScrollContainer
      caption={caption}
      cellWidth={cellWidth}
      className={clsx(styles.wrapper, className)}
      data-cxy-name="stats-table"
      maxRows={maxRows}
      {...props}
    >
      <Table className={styles.table} data-testid="stats-table">
        {children}
      </Table>
    </TableScrollContainer>
  );
};

const StatsTableRow = ({ children, className, ...props }: ThHTMLAttributes<HTMLTableRowElement>) => (
  <TableRow className={clsx(styles.tableRow, className)} size="s" {...props}>
    {children}
  </TableRow>
);

const StatsTableColumn = ({
  children,
  className,
  justifyContent = 'center',
  pinned,
  ...props
}: { pinned?: boolean } & TableColumnType) => (
  <TableColumn
    className={clsx(styles.tableColumn, className)}
    data-justify={justifyContent}
    data-pinned={pinned}
    data-type="heading"
    justifyContent={justifyContent}
    {...props}
  >
    {children}
  </TableColumn>
);

const StatsTableCell = ({
  children,
  className,
  justifyContent = 'center',
  pinned,
  ...props
}: { pinned?: boolean } & TableCellType) => (
  <TableCell
    className={clsx(styles.tableCell, className)}
    data-highlight={!!props.highlight}
    data-justify={justifyContent}
    data-pinned={pinned}
    justifyContent={justifyContent}
    {...props}
  >
    {children}
  </TableCell>
);

const StatsTableFooter = ({ children, className, ...props }: TableFooterType) => (
  <TableFooter {...props} className={clsx(styles.tableFooter, className)}>
    {children}
  </TableFooter>
);

const StatsTableHead = ({ children, className, ...props }: TableHeadType) => (
  <TableHead {...props} className={clsx(styles.tableHead, className)}>
    {children}
  </TableHead>
);

const StatsTableBody = ({ children, className, ...props }: TableBodyType) => (
  <TableBody className={clsx(styles.tableBody, className)} {...props}>
    {children}
  </TableBody>
);

StatsTable.Row = StatsTableRow;
StatsTable.Head = StatsTableHead;
StatsTable.Body = StatsTableBody;
StatsTable.Column = StatsTableColumn;
StatsTable.Cell = StatsTableCell;
StatsTable.Footer = StatsTableFooter;

export { StatsTable };
